import FormattedMessage from 'I18n/components/FormattedMessage';
import getLang from 'I18n/utils/getLang';
import { DataSyncIcon } from 'foundations-assets/icons/components/DataSyncIcon';
import { Flex } from 'foundations-components/components/Flex';
import { Text } from 'foundations-components/components/Text';
import { View } from 'foundations-components/components/View';
import { BreakString } from 'foundations-components/transitional/components/BreakString';
import { Button } from 'foundations-components/transitional/components/Button';
import { ExpandableText } from 'foundations-components/transitional/components/ExpandableText';
import { Tab, Tabs } from 'foundations-components/transitional/components/Tabs';
import { useCallback, useMemo, useState } from 'react';
import SanitizedText from 'sanitize-text/components/SanitizedText';
import { EcosystemListingStub } from 'trellis-ecosystem';
import { useDetailsPageContext } from 'marketplace-ui-common/context/DetailsPageContext';
import { AppCard } from '../../functionality/AppCard';
import { McpServerTab } from '../../functionality/McpServerTab';
import { SharedDataTable } from '../../functionality/SharedDataTable';
import { ToolsList } from '../../functionality/ToolsList';
import { EmbeddedAcademyVideo } from '../../media/EmbeddedAcademyVideo';
import { getDataSyncProductUrl } from 'marketplace-ui-common/utils/DetailsPageUrls';
import { DetailsPageCard } from './DetailsPageCard';
import { AutomationsTab } from '../../functionality/AutomationsTab';
import { gate } from 'hub-http/gates';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
export function ProductFunctionalityCard({
  appCards,
  academyVideoIds,
  automationActions,
  automationListings,
  mcpServer,
  sharedDataHow,
  sharedDataInfo,
  sharedObjects,
  appObjects,
  recommendations,
  tools
}) {
  var _userInfo$gates, _tabs$, _tabs$2;
  const {
    previewListingId,
    productType,
    trackInteraction,
    userInfo
  } = useDetailsPageContext();
  const isUngatedForWorkflowTemplatesPhase2 = (userInfo === null || userInfo === void 0 || (_userInfo$gates = userInfo.gates) === null || _userInfo$gates === void 0 ? void 0 : _userInfo$gates.includes(gate('Marketplace::WorkflowTemplates::Phase2'))) || previewListingId; //TODO_TEMPLATES: Maybe a better alternative to this check for preview, investigate why gating isn't working in preview

  const handleTrackInteraction = useCallback(eventProps => {
    trackInteraction(Object.assign({
      section: 'product functionality card'
    }, eventProps));
  }, [trackInteraction]);
  const renderedDataSyncHowItWorks = useMemo(() => sharedDataHow && /*#__PURE__*/_jsxs(View, {
    marginBlockEnd: "600",
    children: [/*#__PURE__*/_jsx(BreakString, {
      children: /*#__PURE__*/_jsx(SanitizedText, {
        body: sharedDataHow
      })
    }), /*#__PURE__*/_jsxs(Button, {
      use: "tertiary-light",
      size: "sm",
      marginBlockStart: "600",
      external: true,
      href: getDataSyncProductUrl(getLang()),
      children: [/*#__PURE__*/_jsx(DataSyncIcon, {
        color: "fill-brand-default"
      }), /*#__PURE__*/_jsx(FormattedMessage, {
        message: "detailsPage.functionalityCard.tabs.sharedData.dataSyncByHubSpot"
      })]
    })]
  }), [sharedDataHow]);
  const renderedDataSyncInfo = useMemo(() => sharedDataInfo && /*#__PURE__*/_jsxs(Flex, {
    flexDirection: "column",
    gap: "200",
    marginBlockStart: "600",
    children: [/*#__PURE__*/_jsx(Text, {
      typography: "heading-50",
      children: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "detailsPage.functionalityCard.tabs.sharedData.moreInfo"
      })
    }), /*#__PURE__*/_jsx(BreakString, {
      children: /*#__PURE__*/_jsx(SanitizedText, {
        body: sharedDataInfo
      })
    })]
  }), [sharedDataInfo]);
  const renderedRecommendedProducts = useMemo(() => recommendations && /*#__PURE__*/_jsxs(Flex, {
    flexDirection: "column",
    gap: "400",
    marginBlockStart: "800",
    children: [/*#__PURE__*/_jsxs(Flex, {
      flexDirection: "column",
      gap: "100",
      children: [/*#__PURE__*/_jsx(Text, {
        as: "h5",
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: `detailsPage.functionalityCard.tabs.recommendations.${productType}.title`
        })
      }), /*#__PURE__*/_jsx(Text, {
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: `detailsPage.functionalityCard.tabs.recommendations.${productType}.description`
        })
      })]
    }), /*#__PURE__*/_jsx(Flex, {
      flexDirection: "row",
      gap: "400",
      marginBlockStart: "200",
      marginInlineStart: "200",
      flexWrap: "wrap",
      children: recommendations.map((rec, index) => /*#__PURE__*/_jsx(Flex, {
        inlineSize: "30%",
        children: /*#__PURE__*/_jsx(EcosystemListingStub, {
          imageSrc: rec.imageSrc,
          listingHref: rec.listingHref,
          name: rec.name,
          productType: rec.productType,
          providerName: rec.providerName,
          actions: rec.actions,
          compact: true
        }, index)
      }, index))
    })]
  }), [recommendations, productType]);
  const tabs = useMemo(() => {
    const tabsArray = [];
    if (sharedObjects && sharedObjects.length > 0) {
      tabsArray.push({
        tabKey: 'sharedData',
        content: /*#__PURE__*/_jsxs(_Fragment, {
          children: [renderedDataSyncHowItWorks, /*#__PURE__*/_jsx(SharedDataTable, {
            onTrackInteraction: handleTrackInteraction,
            sharedObjects: sharedObjects,
            appObjects: appObjects
          }), renderedDataSyncInfo]
        }),
        showExpand: false
      });
    }
    if (isUngatedForWorkflowTemplatesPhase2 && (automationListings && automationListings.length > 0 || automationActions && automationActions.length > 0)) {
      tabsArray.push({
        tabKey: 'automations',
        content: /*#__PURE__*/_jsx(AutomationsTab, {
          automationActions: automationActions || [],
          automationListings: automationListings || []
        }),
        showExpand: false
      });
    }
    if (tools && tools.length > 0) {
      tabsArray.push({
        tabKey: 'tools',
        content: /*#__PURE__*/_jsxs(_Fragment, {
          children: [/*#__PURE__*/_jsx(ToolsList, {
            tools: tools
          }), renderedRecommendedProducts]
        }),
        showExpand: false
      });
    }
    if (mcpServer) {
      tabsArray.push({
        tabKey: 'mcpServer',
        content: /*#__PURE__*/_jsx(McpServerTab, {
          mcpServer: mcpServer
        }),
        showExpand: false
      });
    }
    if (appCards && appCards.length > 0) {
      tabsArray.push({
        tabKey: 'appCards',
        content: /*#__PURE__*/_jsx(_Fragment, {
          children: appCards.map((appCard, index) => /*#__PURE__*/_jsx(AppCard, {
            appCard: appCard,
            showBottomBorder: index !== appCards.length - 1
          }, `${appCard.name}_${index}`))
        }),
        showExpand: appCards.length > 2
      });
    }
    if (academyVideoIds && academyVideoIds.length > 0) {
      tabsArray.push({
        tabKey: 'howItWorks',
        content: /*#__PURE__*/_jsx(_Fragment, {
          children: academyVideoIds.map((id, index) => /*#__PURE__*/_jsx(EmbeddedAcademyVideo, {
            onTrackInteraction: handleTrackInteraction,
            id: id
          }, index))
        }),
        showExpand: academyVideoIds.length > 2
      });
    }
    return tabsArray;
  }, [sharedObjects, isUngatedForWorkflowTemplatesPhase2, automationListings, automationActions, tools, mcpServer, appCards, academyVideoIds, renderedDataSyncHowItWorks, handleTrackInteraction, appObjects, renderedDataSyncInfo, renderedRecommendedProducts]);
  const [selectedTab, setSelectedTab] = useState((_tabs$ = tabs[0]) === null || _tabs$ === void 0 ? void 0 : _tabs$.tabKey);
  if (tabs.length === 0) {
    return null;
  }
  return /*#__PURE__*/_jsx(DetailsPageCard, {
    id: "product-functionality-card",
    name: "functionality card",
    title: /*#__PURE__*/_jsx(FormattedMessage, {
      message: `detailsPage.functionalityCard.title.${productType}`
    }),
    children: tabs.length > 1 ? /*#__PURE__*/_jsx(Tabs, {
      selected: selectedTab,
      onSelectedChange: event => {
        handleTrackInteraction({
          action: 'change tab',
          value: event.target.value
        });
        setSelectedTab(event.target.value);
      },
      panelStyles: {
        paddingInline: '0'
      },
      children: tabs.map(tab => {
        const {
          tabKey,
          content
        } = tab;
        return /*#__PURE__*/_jsx(Tab, {
          tabId: tabKey,
          title: /*#__PURE__*/_jsx(FormattedMessage, {
            message: `detailsPage.functionalityCard.tabs.${tabKey}.name`
          }),
          children: tab.showExpand ? /*#__PURE__*/_jsx(ExpandableText, {
            maxHeight: 400,
            hideButtonCaret: true,
            buttonAlign: "left",
            children: content
          }) : content
        }, tabKey);
      })
    }) : /*#__PURE__*/_jsxs("div", {
      children: [/*#__PURE__*/_jsx(Text, {
        as: "h5",
        paddingBlockStart: "600",
        paddingBlockEnd: "200",
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: `detailsPage.functionalityCard.tabs.${(_tabs$2 = tabs[0]) === null || _tabs$2 === void 0 ? void 0 : _tabs$2.tabKey}.name`
        })
      }), tabs[0].content]
    })
  });
}